import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'motion/react';
import { Plus, Minus } from 'lucide-react';
import SiteNav from '../components/SiteNav';
import SiteFooter from '../components/SiteFooter';
import { useSEO } from '../hooks/useSEO';

interface FAQItem {
  question: string;
  answer: string;
}

const FAQ_ITEMS: FAQItem[] = [
  {
    question: 'What is Aura Traveler?',
    answer:
      'Aura Traveler is an AI-powered luxury travel design platform that generates magazine-quality travel itineraries in under 90 seconds. It is built for luxury travel agencies, hotel concierge teams, and discerning individual travelers who refuse to settle for generic itinerary builders.',
  },
  {
    question: 'What is a travel itinerary?',
    answer:
      'A travel itinerary is a comprehensive, curated document that consolidates every element of a journey — accommodations, dining, logistics, cultural context, packing recommendations, and booking links — into one cohesive, publication-quality artifact. Unlike a basic schedule, a luxury itinerary is an editorial experience designed to be read, shared, and treasured.',
  },
  {
    question: 'How long does it take to generate an itinerary?',
    answer:
      'Aura generates a complete, AI-curated itinerary in under 90 seconds. The full day-by-day itinerary with images, recommendations, and logistics is typically ready in under 3 minutes depending on destination complexity and selected options.',
  },
  {
    question: 'Can I white-label the itineraries with my agency branding?',
    answer:
      'Yes. Agency and Enterprise plans include full white-label support. You can apply your agency name, logo, color palette, and custom messaging to every itinerary. Your clients will see your brand, not Aura\'s.',
  },
  {
    question: 'How accurate are the booking links?',
    answer:
      'Aura\'s link validation system checks and verifies booking links at generation time. Every hotel, restaurant, and venue link in your itinerary routes to a live, correct booking page. We treat booking link integrity as a non-negotiable — broken links are not acceptable in the luxury tier.',
  },
  {
    question: 'What is the difference between Aura and tools like Axus or Vamoos?',
    answer:
      'Traditional itinerary builders like Axus and Vamoos are organizational tools — they help you structure content you write yourself. Aura is a generative intelligence engine: it creates the content, the visuals, the recommendations, and the design. You direct it; it produces. The resulting documents are meaningfully higher quality than what template-filling software can produce.',
  },
  {
    question: 'Can Aura handle complex multi-destination itineraries?',
    answer:
      'Yes. Aura is designed for complex, multi-destination luxury journeys. It handles route optimization, inter-destination logistics, and multi-property accommodations across every region it supports — currently over 140 countries.',
  },
  {
    question: 'Is my client data secure?',
    answer:
      'Yes. Aura uses Firebase for secure authentication and data storage, with industry-standard encryption at rest and in transit. We do not sell or share client data with third parties. For enterprise deployments, we offer additional data residency and compliance configurations.',
  },
  {
    question: 'What does the "Atrium" experience refer to?',
    answer:
      'The Atrium is Aura\'s AI curation mode — an immersive interface where you select, curate, and refine every element of an itinerary before generating the final document. It is the step between intent and artifact: a collaborative creative session between your expertise and Aura\'s intelligence.',
  },
  {
    question: 'Do you offer a free trial?',
    answer:
      'Yes. New users can explore the platform and generate a limited itinerary at no cost. Paid plans unlock higher volumes, premium visuals, and white-label branding. You can upgrade at any time from within your account.',
  },
  {
    question: 'Is Aura available for hotel concierge teams?',
    answer:
      'Yes. Aura has a dedicated solution for hotels and resorts. Concierge teams can generate bespoke, branded itineraries for guests that extend the luxury of the property into the surrounding world. For details, see the For Hotels page or contact us at concierge@auratraveler.com.',
  },
];

const FAQRow: React.FC<{ item: FAQItem; index: number }> = ({ item, index }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-white/5 last:border-b-0">
      <button
        className="w-full flex justify-between items-center py-8 text-left group"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
      >
        <span className="font-display text-lg md:text-xl text-white group-hover:text-gold transition-colors duration-300 pr-8">
          {item.question}
        </span>
        <span className="flex-shrink-0 w-8 h-8 rounded-full border border-white/10 flex items-center justify-center text-white/40 group-hover:border-gold/50 group-hover:text-gold transition-all duration-300">
          {open ? <Minus className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
        </span>
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: 'easeInOut' }}
            className="overflow-hidden"
          >
            <p className="font-serif text-base text-white/70 leading-relaxed pb-8 max-w-3xl">
              {item.answer}
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const FaqPage: React.FC = () => {
  useSEO({
    title: 'FAQ — Frequently Asked Questions | Aura Traveler',
    description:
      'Answers to common questions about Aura Traveler, travel itineraries, pricing, white-label features, and how AI is transforming luxury travel design.',
    canonical: 'https://www.auratraveler.com/faq',
    breadcrumbs: [
      { name: 'Home', url: 'https://www.auratraveler.com/' },
      { name: 'FAQ', url: 'https://www.auratraveler.com/faq' },
    ],
  });

  // JSON-LD FAQ schema
  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: FAQ_ITEMS.map((item) => ({
      '@type': 'Question',
      name: item.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.answer,
      },
    })),
  };

  return (
    <div className="min-h-screen bg-charcoal text-white font-sans selection:bg-gold/30 selection:text-white">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
      />

      <SiteNav />

      <main>
      {/* Hero */}
      <section className="pt-40 pb-16 px-4 sm:px-6 md:px-12 lg:px-16">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: 'easeOut' }}
          >
            <span className="font-sans text-[10px] uppercase tracking-[0.5em] text-gold/80 mb-8 block">
              Questions & Answers
            </span>
            <h1 className="font-display text-4xl sm:text-6xl md:text-7xl text-white leading-[0.92] tracking-tighter font-bold mb-8">
              Frequently{' '}
              <span className="font-serif italic font-medium text-gold">Asked</span>
            </h1>
            <p className="font-serif text-xl text-white/70 leading-relaxed max-w-xl">
              Everything you need to know about Aura Traveler, travel itineraries, and our approach to
              luxury travel design.
            </p>
          </motion.div>
        </div>
      </section>

      {/* FAQ List */}
      <section className="py-12 px-4 sm:px-6 md:px-12 lg:px-16 border-t border-white/5">
        <div className="max-w-4xl mx-auto">
          {FAQ_ITEMS.map((item, i) => (
            <FAQRow key={i} item={item} index={i} />
          ))}
        </div>
      </section>

      {/* Still have questions */}
      <section className="py-24 px-4 sm:px-6 md:px-12 lg:px-16 bg-[#0A0A0A] border-t border-white/5">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="font-display text-3xl md:text-5xl text-white tracking-tighter mb-6">
            Still have questions?
          </h2>
          <p className="font-serif text-lg text-white/70 mb-10">
            Our team is available at{' '}
            <a
              href="mailto:concierge@auratraveler.com"
              className="text-gold hover:text-white transition-colors duration-300 underline decoration-gold/30"
            >
              concierge@auratraveler.com
            </a>{' '}
            — typically responding within 24 hours.
          </p>
          <div className="flex flex-col sm:flex-row gap-6 justify-center">
            <Link
              to="/pricing"
              className="px-10 py-4 bg-gold text-black font-sans text-[10px] font-bold uppercase tracking-[0.3em] hover:bg-white transition-all duration-500 rounded-full"
            >
              View Pricing
            </Link>
            <Link
              to="/philosophy"
              className="px-10 py-4 border border-white/20 text-white font-sans text-[10px] font-bold uppercase tracking-[0.3em] hover:border-gold hover:text-gold transition-all duration-500 rounded-full"
            >
              Read Our Philosophy
            </Link>
          </div>
        </div>
      </section>

      </main>

      <SiteFooter />
    </div>
  );
};

export default FaqPage;
